import { FC, useEffect, useState } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Select } from '@forwardflow/ui-v2'
import { DashboardContainer } from '@forwardflow/components/Dashboard'
import Conversations from '@forwardflow/components/Dashboard/Conversations'
import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { useContactDetails } from '@forwardflow/hooks/customHooks/conversation/useContactDetails'
import { LeadList } from '@forwardflow/components/Dashboard/LeadList'
import { getStorageKey } from '@forwardflow/helpers/utils'
import { useActiveCampaigns } from '@forwardflow/hooks/customHooks/campaign/getActiveCampaigns'
import { SelectProps } from '@mantine/core'
import cn from 'classnames'
import { useAppStore } from '@forwardflow/store'

const CONTACT_FILTER_OPTIONS = [
   {
      label: 'All Appointments',
      value: 'all-stages',
   },
   {
      label: 'Appointment Set',
      value: 'ff-appointment-set',
   },
   {
      label: 'Appointment Missed',
      value: 'ff-appointment-missed',
   },
]

const renderSelectOption: SelectProps['renderOption'] = ({
   option,
   checked,
}) => (
   <div
      className={cn('p-0.5', {
         'text-white/80': checked,
      })}
   >
      {option.label}
   </div>
)

const LEADS_FILTER_OPTIONS = [
   { value: 'allLeads', label: 'All Leads' },
   { value: 'engagedLead', label: 'All Engaged Leads' },
   { value: 'liveTransfer', label: 'Live Transfers' },
   { value: 'incomingCall', label: 'Incoming Calls' },
   { value: 'callOver30s', label: 'Call Conversations (>30s)' },
   { value: 'textConversations', label: 'Text Conversations' },
]

const ConversationsPage: FC = () => {
   const { id } = useParams()
   const { setCurrentOrganization } = useAppStore()
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const organizationId = searchParams.get('organizationId')
   // Get values from search params instead of URL params
   const campaign = searchParams.get('filter')
   const campaignIdParam = searchParams.get('campaignId')
   const filter = searchParams.get('stages')
   const leadFilterParam = searchParams.get('leadFilter')
   const [selectedCampaignIds, setSelectedCampaignIds] = useState<
      string[] | null
   >(null)

   const { data: activeCampaigns } = useActiveCampaigns({
      page: 1,
      limit: 10,
      status: 'active,paused',
   })

   const isDemo = window.location.pathname.includes('demo-conversations-old')

   const getCampaignOptions = () => {
      const defaultOption = {
         label: isDemo ? 'Demo Source' : 'All Campaigns',
         value: isDemo
            ? activeCampaigns?.find((s) => s?.campaignName === 'Demo Source')
                 ?.id ?? 'Demo Source'
            : 'all-campaigns',
      }

      const allActiveCampaign = {
         label: 'All Active Campaigns',
         value: 'all-active-campaigns',
      }

      const campaignList = activeCampaigns?.filter((source) => {
         if (isDemo) {
            return source?.campaignName !== 'Demo Source'
         }
         return true
      })
      const allActiveCampaignList =
         campaignList
            ?.filter((source) => source?.status === 'active')
            ?.map((source) => ({
               label: source?.campaignName || '',
               value: source?.id || '',
            })) || []

      const otherCampaignList =
         campaignList
            ?.filter((campaign) => campaign?.status !== 'active')
            ?.map((campaign) => ({
               label: campaign?.campaignName || '',
               value: campaign?.id || '',
            })) || []

      return {
         allActiveCampaign: allActiveCampaignList,
         otherCampaign: otherCampaignList,
         noneGroupList: [defaultOption, allActiveCampaign],
      }
   }

   const getInitialCampaignId = () => {
      // If campaignId param exists (comma-separated IDs), use it directly
      if (campaignIdParam) {
         setSelectedCampaignName(null)
         return campaignIdParam
      }

      if (!campaign || campaign === 'all-campaign') {
         setSelectedCampaignName(null)
         return null
      }

      const decodedCampaign = decodeURIComponent(campaign)

      if (activeCampaigns) {
         const foundByCampaignName = activeCampaigns.find(
            (source) => source?.campaignName === decodedCampaign
         )

         const foundById = activeCampaigns.find(
            (source) => source?.id === decodedCampaign
         )

         const foundCampaign = foundByCampaignName || foundById

         if (foundCampaign) {
            setSelectedCampaignName(foundCampaign.campaignName)
            return foundCampaign.id
         }
      }
      setSelectedCampaignName(decodedCampaign)
      return decodedCampaign
   }

   useEffect(() => {
      if (activeCampaigns && selectedCampaignId === null) {
         const initialId = getInitialCampaignId()
         setSelectedCampaignId(initialId)
      }
   }, [activeCampaigns, campaign])

   const getInitialStageFilter = () => {
      if (filter === 'apt-set') return 'ff-appointment-set'
      if (filter === 'apt-missed') return 'ff-appointment-missed'
      return 'all-stages'
   }

   const [selectedStageFilter, setSelectedStageFilter] = useState<string>(
      getInitialStageFilter()
   )

   const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
      null
   )
   const [selectedCampaignName, setSelectedCampaignName] = useState<
      string | null
   >(null)

   const [selectedLeadFilter, setSelectedLeadFilter] = useState<string>(
      leadFilterParam || 'allLeads'
   )

   const handleStageFilterSelect = (stage: string) => {
      setSelectedStageFilter(stage)
      let newSearchFilter = 'all-stages'
      if (stage === 'ff-appointment-set') {
         newSearchFilter = 'apt-set'
      } else if (stage === 'ff-appointment-missed') {
         newSearchFilter = 'apt-missed'
      }

      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const basePath = isDemo ? '/demo-conversations-old' : '/conversations'

      const storageKey = getStorageKey(stage, selectedCampaignName)
      const storedLeadId = localStorage.getItem(storageKey)

      // Build search params
      const params = new URLSearchParams()
      if (newSearchFilter !== 'all-stages') {
         params.set('stages', newSearchFilter)
      }
      if (
         selectedCampaignName &&
         selectedCampaignName !== (isDemo ? 'Demo Source' : null)
      ) {
         params.set('filter', selectedCampaignName)
      }
      // Preserve lead filter
      if (selectedLeadFilter !== 'allLeads') {
         params.set('leadFilter', selectedLeadFilter)
      }
      // Preserve organizationId
      if (organizationId) {
         params.set('organizationId', organizationId)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      if (storedLeadId) {
         navigate(`${basePath}/${storedLeadId}${urlSuffix}`)
      } else {
         navigate(`${basePath}${urlSuffix}`)
         setSelectedContactLeadId(null)
      }
   }

   const handleLeadFilterSelect = (value: string) => {
      setSelectedLeadFilter(value)
      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const basePath = isDemo ? '/demo-conversations-old' : '/conversations'

      // Build search params
      const params = new URLSearchParams()
      // Add stage filter if not default
      let stageParam = 'all-stages'
      if (selectedStageFilter === 'ff-appointment-set') {
         stageParam = 'apt-set'
      } else if (selectedStageFilter === 'ff-appointment-missed') {
         stageParam = 'apt-missed'
      }
      if (stageParam !== 'all-stages') {
         params.set('stages', stageParam)
      }

      // Add campaign filter if selected
      if (
         selectedCampaignName &&
         selectedCampaignName !== (isDemo ? 'Demo Source' : null)
      ) {
         params.set('filter', selectedCampaignName)
      }

      // Add lead filter if not "allLeads"
      if (value !== 'allLeads') {
         params.set('leadFilter', value)
      }

      // Preserve organizationId
      if (organizationId) {
         params.set('organizationId', organizationId)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      // Navigate maintaining the current lead ID if exists
      if (id) {
         navigate(`${basePath}/${id}${urlSuffix}`)
      } else {
         navigate(`${basePath}${urlSuffix}`)
      }
   }

   useEffect(() => {
      if (organizationId && setCurrentOrganization) {
         setCurrentOrganization(organizationId)
      }
   }, [organizationId, setCurrentOrganization])

   useEffect(() => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const isDemoCampaign = campaign === 'Demo Source'

      if (isDemo && !isDemoCampaign) {
         const basePath = '/demo-conversations-old'
         const params = new URLSearchParams()

         if (filter && filter !== 'all-stages') {
            params.set('stages', filter)
         }
         params.set('filter', 'Demo Source')
         // Preserve lead filter
         if (leadFilterParam && leadFilterParam !== 'allLeads') {
            params.set('leadFilter', leadFilterParam)
         }
         // Preserve organizationId
         if (organizationId) {
            params.set('organizationId', organizationId)
         }

         const queryString = params.toString()
         const urlSuffix = queryString ? `?${queryString}` : ''

         if (id) {
            navigate(`${basePath}/${id}${urlSuffix}`, { replace: true })
         } else {
            navigate(`${basePath}${urlSuffix}`, { replace: true })
         }
      }
   }, [
      window.location.pathname,
      campaign,
      filter,
      id,
      leadFilterParam,
      organizationId,
      navigate,
   ])

   useEffect(() => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')

      if (isDemo) {
         const demoSourceCampaign = activeCampaigns?.find(
            (source) => source?.campaignName === 'Demo Source'
         )

         const demoSourceId = demoSourceCampaign?.id ?? 'Demo Source'

         // const demoSourceId = '4494a616f35e141a27f9b2bb41b15204'

         if (
            selectedCampaignId !== demoSourceId ||
            selectedCampaignName !== 'Demo Source'
         ) {
            setSelectedCampaignId(demoSourceId)
            setSelectedCampaignName('Demo Source')
         }
      }
   }, [
      window.location.pathname,
      activeCampaigns,
      selectedCampaignId,
      selectedCampaignName,
   ])

   const [selectedContactLeadId, setSelectedContactLeadId] = useState<
      number | null
   >(id ? Number(id) : null)

   const {
      data: selectedContactDetails,
      isLoading,
      error,
   } = useContactDetails(id ? Number(id) : null)

   const handleLeadSelect = (leadId: number | null) => {
      setSelectedContactLeadId(leadId)

      if (leadId === null) {
         const basePath = window.location.pathname.includes(
            'demo-conversations-old'
         )
            ? '/demo-conversations-old'
            : '/conversations'

         let updatedSearchFilter = 'all-stages'
         if (selectedStageFilter === 'ff-appointment-set') {
            updatedSearchFilter = 'apt-set'
         } else if (selectedStageFilter === 'ff-appointment-missed') {
            updatedSearchFilter = 'apt-missed'
         }

         // Build search params
         const params = new URLSearchParams()
         if (updatedSearchFilter !== 'all-stages') {
            params.set('stages', updatedSearchFilter)
         }
         if (selectedCampaignName) {
            params.set('filter', selectedCampaignName)
         }
         // Preserve lead filter
         if (selectedLeadFilter && selectedLeadFilter !== 'allLeads') {
            params.set('leadFilter', selectedLeadFilter)
         }
         // Preserve organizationId
         if (organizationId) {
            params.set('organizationId', organizationId)
         }

         const queryString = params.toString()
         const urlSuffix = queryString ? `?${queryString}` : ''

         navigate(`${basePath}${urlSuffix}`)
      }
   }

   const getLeadDetailsForConversation = () => {
      if (selectedContactLeadId === null) {
         return null
      }

      const isDemo = window.location.pathname.includes('demo-conversations-old')
      if (
         isDemo &&
         selectedContactDetails?.sourceInfo?.name !== 'Demo Source'
      ) {
         return undefined
      }

      if (error) {
         return undefined
      }
      return selectedContactDetails
   }

   const onSelectCampaign = (value: string) => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')

      if (isDemo) {
         return
      }

      // Determine new campaign name for URL and storage
      let newCampaignName: string | null = null

      // Handle "all-campaigns" selection
      if (value === 'all-campaigns') {
         setSelectedCampaignIds(null)
      } else if (value === 'all-active-campaigns') {
         setSelectedCampaignIds(
            activeCampaigns
               ?.filter((source) => source?.status === 'active')
               ?.map((source) => source?.id) || []
         )
      } else {
         setSelectedCampaignIds([value])
         const campaign = activeCampaigns?.find((source) => source?.id === value)
         newCampaignName = campaign?.campaignName || value
      }

      setSelectedCampaignName(newCampaignName)

      let currentFilter = 'all-stages'
      if (selectedStageFilter === 'ff-appointment-set') {
         currentFilter = 'apt-set'
      } else if (selectedStageFilter === 'ff-appointment-missed') {
         currentFilter = 'apt-missed'
      }

      const storageKey = getStorageKey(
         selectedStageFilter,
         newCampaignName
      )
      const storedLeadId = localStorage.getItem(storageKey)

      // Build search params
      const params = new URLSearchParams()
      if (currentFilter !== 'all-stages') {
         params.set('stages', currentFilter)
      }

      // Add campaign filter
      if (newCampaignName) {
         params.set('filter', newCampaignName)
      }

      // Preserve lead filter
      if (selectedLeadFilter && selectedLeadFilter !== 'allLeads') {
         params.set('leadFilter', selectedLeadFilter)
      }

      // Preserve organizationId
      if (organizationId) {
         params.set('organizationId', organizationId)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      if (storedLeadId) {
         navigate(`/conversations/${storedLeadId}${urlSuffix}`)
      } else {
         navigate(`/conversations${urlSuffix}`)
         setSelectedContactLeadId(null)
      }
   }

   return (
      <>
         <style>{`html { overflow: hidden; }`}</style>
         <DashboardContainer>
            <div className="bg-white/5 min-h-[52px] rounded-lg flex items-center mb-4">
               <Link to="/dashboard">
                  <Button className="!w-[36px] h-[36px] border border-transparent bg-transparent !rounded-lg !mt-0 ml-2 mr-4">
                     <Icon
                        name="arrow-right"
                        className="rotate-180 fill-white"
                        width={20}
                        height={20}
                     />
                  </Button>
               </Link>
               <span className="text-[#717171]">
                  <Link to="/dashboard"> Home /</Link>
                  <span className="text-white"> Conversations</span>
               </span>
               <div className="flex flex-wrap items-center gap-2 pr-2 ml-auto">
                  <Select
                     data={[
                        {
                           group: '',
                           items: getCampaignOptions()?.noneGroupList || [],
                        },
                        {
                           group: 'ACTIVE CAMPAIGNS',
                           items: getCampaignOptions()?.allActiveCampaign || [],
                        },
                        {
                           group: 'OTHER CAMPAIGNS',
                           items: getCampaignOptions()?.otherCampaign || [],
                        },
                     ]}
                     value={selectedCampaignId}
                     onChange={(value) => {
                        if (!value) return

                        setSelectedCampaignId(value)

                        onSelectCampaign(value)
                     }}
                     radius="md"
                     withCheckIcon={false}
                     renderOption={renderSelectOption}
                     placeholder={isDemo ? 'Demo Source' : 'All Campaigns'}
                     classNames={{
                        input: '!w-[200px] !h-9 !bg-[#594180] !border-[#594180] text-white',
                        dropdown:
                           '!bg-white/50 !rounded-lg !p-1 backdrop-blur-[12.45px]',
                        groupLabel: 'text-white !text-[10px]',
                        option: 'hover:!bg-white/10 !text-white !rounded',
                     }}
                     comboboxProps={{
                        transitionProps: { transition: 'fade', duration: 100 },
                     }}
                  />
                  <Select
                     data={LEADS_FILTER_OPTIONS}
                     value={selectedLeadFilter}
                     onChange={(value) => {
                        if (value) handleLeadFilterSelect(value)
                     }}
                     radius="md"
                     withCheckIcon={false}
                     renderOption={renderSelectOption}
                     classNames={{
                        input: '!w-[200px] !h-9 !bg-[#594180] !border-[#594180] text-white',
                        dropdown:
                           '!bg-white/50 !rounded-lg !p-1 backdrop-blur-[12.45px]',
                        option: 'hover:!bg-white/10 !text-white !rounded',
                     }}
                     comboboxProps={{
                        transitionProps: { transition: 'fade', duration: 100 },
                     }}
                  />
                  <Select
                     data={CONTACT_FILTER_OPTIONS}
                     value={selectedStageFilter}
                     onChange={(value) => {
                        if (value) handleStageFilterSelect(value)
                     }}
                     renderOption={renderSelectOption}
                     withCheckIcon={false}
                     radius="md"
                     classNames={{
                        input: '!w-[200px] !h-9 !bg-[#594180] !border-[#594180] text-white',
                        dropdown:
                           '!bg-white/50 !rounded-lg backdrop-blur-[49.45px]',
                        option: 'hover:!bg-white/10 !text-white !rounded',
                     }}
                     comboboxProps={{
                        transitionProps: { transition: 'fade', duration: 100 },
                     }}
                  />
               </div>
            </div>
            <div className="flex flex-col w-full gap-5 lg:flex-row h-[calc(100%-80px)]">
               <LeadList
                  onSelectLead={handleLeadSelect}
                  selectedStageFilter={selectedStageFilter}
                  selectedCampaignIds={selectedCampaignIds}
                  selectedCampaignName={selectedCampaignName}
                  selectedLeadFilter={selectedLeadFilter}
               />
               <div className="flex-1 flex-grow h-full">
                  <Conversations
                     selectedLeadDetails={getLeadDetailsForConversation()}
                     isLoading={isLoading}
                  />
               </div>
            </div>
         </DashboardContainer>
      </>
   )
}

export default ConversationsPage
